<template>
  <v-card
    :disabled="isSubmitting"
  >
    <v-card-title>
      Edit #{{ itemId }}
    </v-card-title>
    
    <v-skeleton-loader
      v-if="isLoading || !item"
      height="94"
      type="list-item-two-line"
    />
    <v-form
      v-else
      ref="form"
      v-on:submit.prevent="submit"
    >
      <v-container>
        <v-row>

          <v-col
            cols="12"
            md="12"
            class="d-flex justify-center flex-wrap"
          >

            <!-- DATE -->
            <div
              class="btn-group-container"
            >
              <div class="btn-group-title">Date</div>
              <v-btn-toggle
                color="primary"
              >
                <j-date-field
                  v-model.lazy="item.date"
                  :hint="!item.timeGroupId ? 'not selected' : timeGroups.find(tg => tg.id == item.timeGroupId).name"
                  persistent-hint
                >
                  <v-btn
                    :class="{ 'v-btn--active': !!item.timeGroupId }"
                  >
                    {{ item.date || 'Select Date' }}
                  </v-btn>
                </j-date-field>
              </v-btn-toggle>
              <div class="btn-group-note">{{ item.date | long-date }}</div>
            </div>

            <!-- TIME GROUP -->
            <div
              class="btn-group-container"
            >
              <div class="btn-group-title">Time</div>
              <v-btn-toggle
                v-model="item.timeGroupId"
                color="primary"
              >
                <v-tooltip
                  v-for="timeGroup in timeGroups"
                  :key="timeGroup.id"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      :value="timeGroup.id"
                      v-on="on"
                    >
                      <v-icon v-text="timeGroup.icon" style="color: inherit !important;" />
                    </v-btn>
                  </template>
                  <span v-text="timeGroup.name" />
                </v-tooltip>
              </v-btn-toggle>
              <div class="btn-group-note">{{ findName(item.timeGroupId, timeGroups) }}</div>
            </div>

            <!-- TYPE -->
            <div
              class="btn-group-container"
            >
              <div class="btn-group-title">Type</div>
              <v-btn-toggle
                v-model="item.typeId"
                color="primary"
              >
                <v-tooltip
                  v-for="type in types"
                  :key="type.id"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      :value="type.id"
                      v-on="on"
                    >
                      {{ type.abbr }}
                    </v-btn>
                  </template>
                  <span v-text="type.name" />
                </v-tooltip>
              </v-btn-toggle>
              <div class="btn-group-note">{{ findName(item.typeId, types) }}</div>
            </div>

            <!-- LOCATION -->
            <div
              class="btn-group-container"
            >
              <div class="btn-group-title">Location</div>
              <div class="d-flex align-center">
                <v-btn-toggle
                  v-if="!calcShowMoreLocations"
                  v-model="item.locationId"
                  color="primary"
                >
                  <v-tooltip
                    v-for="obj in limitedLocations"
                    :key="obj.id"
                    bottom
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :value="obj.id"
                        v-on="on"
                      >
                        {{ obj.abbr }}
                      </v-btn>
                    </template>
                    <span v-text="obj.name" />
                  </v-tooltip>
                </v-btn-toggle>
                <v-tooltip
                  v-if="!calcShowMoreLocations"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      v-on="on"
                      v-on:click="showMoreLocations = true"
                      class="mx-2"
                    >
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <span>More Locations</span>
                </v-tooltip>
                <v-select
                  v-else
                  v-model="item.locationId"
                  :items="locations"
                  item-value="id"
                  item-text="abbr"
                  outlined
                  height="48"
                  dense
                  hide-details
                  style="width: 246px;"
                >
                  <template v-slot:item="{ item }">
                    {{ item.name }} ({{ item.abbr }})
                  </template>
                </v-select>
              </div>
              <div class="btn-group-note">{{ findName(item.locationId, locations) }}</div>
            </div>

            <!-- PLACE -->
            <div
              class="btn-group-container"
            >
              <div class="btn-group-title">Place</div>
              <div class="d-flex align-center">
                <v-btn-toggle
                  v-if="!calcShowMorePlaces"
                  v-model="item.placeId"
                  color="primary"
                >
                  <v-tooltip
                    v-for="obj in limitedPlaces"
                    :key="obj.id"
                    bottom
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :value="obj.id"
                        v-on="on"
                      >
                        {{ obj.abbr }}
                      </v-btn>
                    </template>
                    <span v-text="obj.name" />
                  </v-tooltip>
                </v-btn-toggle>
                <v-tooltip
                  v-if="!calcShowMorePlaces"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      v-on="on"
                      v-on:click="showMorePlaces = true"
                      class="mx-2"
                    >
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <span>More Places</span>
                </v-tooltip>
                <v-select
                  v-else
                  v-model="item.placeId"
                  :items="places"
                  item-value="id"
                  item-text="abbr"
                  outlined
                  height="48"
                  dense
                  hide-details
                  style="width: 246px;"
                >
                  <template v-slot:item="{ item }">
                    {{ item.name }} ({{ item.abbr }})
                  </template>
                </v-select>
              </div>
              <div class="btn-group-note">{{ findName(item.placeId, places) }}</div>
            </div>

            <!-- POSITIONS -->
            <div
              class="btn-group-container"
            >
              <div class="btn-group-title">Positions</div>
              <div class="d-flex align-center">
                <v-btn-toggle
                  v-if="!calcShowMorePositions"
                  v-model="item.positionIds"
                  color="primary"
                  multiple
                >
                  <v-tooltip
                    v-for="obj in limitedPositions"
                    :key="obj.id"
                    bottom
                  >
                    <template v-slot:activator="{ on }">
                      <v-btn
                        :value="obj.id"
                        v-on="on"
                      >
                        {{ obj.abbr }}
                      </v-btn>
                    </template>
                    <span v-text="obj.name" />
                  </v-tooltip>
                </v-btn-toggle>
                <v-tooltip
                  v-if="!calcShowMorePositions"
                  bottom
                >
                  <template v-slot:activator="{ on }">
                    <v-btn
                      icon
                      v-on="on"
                      v-on:click="showMorePositions = true"
                      class="mx-2"
                    >
                      <v-icon>mdi-dots-horizontal</v-icon>
                    </v-btn>
                  </template>
                  <span>More Positions</span>
                </v-tooltip>
                <v-select
                  v-else
                  v-model="item.positionIds"
                  multiple
                  :items="positions"
                  item-value="id"
                  item-text="abbr"
                  outlined
                  height="48"
                  dense
                  hide-details
                  style="width: 246px;"
                >
                  <template v-slot:item="{ item }">
                    {{ item.name }} ({{ item.abbr }})
                  </template>
                </v-select>
              </div>
              <div class="btn-group-note">{{ findName(item.positionIds, positions) }}</div>
            </div>
          </v-col>

          <!-- <v-col
            cols="12"
            md="6"
          >
            <v-select
              v-model="item.locationId"
              :items="locations"
              item-value="id"
              item-text="name"
              label="Location"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
          >
            <v-select
              v-model="item.placeId"
              :items="places"
              item-value="id"
              item-text="name"
              label="Place"
            />
          </v-col>

          <v-col
            cols="12"
          >
            <v-select
              v-model="item.positionIds"
              :items="positions"
              item-value="id"
              item-text="name"
              multiple
              chips
              deletable-chips
              label="Positions"

              persistent-hint
              :hint="item.positionIds.map(positionId => positions.find(position => position.id == positionId).name).join(', ')"
            />
          </v-col> -->

          <v-col cols="12">
            <v-textarea
              v-model="item.note"
              label="Note"
              rows="1"
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
            class="d-flex justify-center align-center"
          >
            <v-rating
              v-model="item.rating"
              length="5"
              dense
              large
              hover
            />
          </v-col>

          <v-col
            cols="12"
            md="6"
            class="d-flex justify-center align-center"
          >
            <v-checkbox
              v-model="item.failure"
              label="Failure"
            />
          </v-col>

        </v-row>

        <!-- <v-row>
          <v-col cols="12">
            {{ Object.keys(item).join(', ') }}
          </v-col>
        </v-row> -->

      </v-container>

      <v-card-actions>
        <v-btn
          v-if="!isNew"
          color="error"
          icon
          :to="{ name: 'item-delete', params: { itemId: item.id } }"
        >
          <v-icon>mdi-delete</v-icon>
        </v-btn>
        <v-spacer />
        <v-btn
          type="submit"
          color="primary"
          :loading="isSubmitting"
        >
          Submit
        </v-btn>
      </v-card-actions>
    </v-form>
  </v-card>
</template>

<script>
// import api from '@/helpers/api'
import { mapGetters } from 'vuex'
import cloneDeep from 'lodash.clonedeep'
import { formatDate } from '@/helpers/dateHelpers'

import JDateField from '@/ui/controls/JDateField'

const isNew = (itemId) => {
  if (itemId === undefined || itemId === null) return true
  return ['new', 'add'].includes(itemId.toString().toLowerCase())
}

const allInList = (val, arr, idFn = z => z.id) => {
  val = val === undefined || val === null ? [] : Array.isArray(val) ? val : [val]
  return val.every(valItem => arr.some(arrItem => idFn(arrItem) == valItem))
}

const DEFAULT_ITEM = {
  id: undefined,
  date: formatDate(new Date()), // new Date().toISOString().split('T')[0],
  rating: undefined,
  note: undefined,
  failure: false,
  timeGroupId: undefined,
  typeId: undefined,
  locationId: undefined,
  placeId: undefined,
  positionIds: [],
}

export default {
  components: {
    JDateField,
  },
  props: {
    itemId: undefined,
  },
  data() {
    return {
      item: undefined,
      isLoading: false,
      isSubmitting: false,

      showMoreLocations: false,
      showMorePlaces: false,
      showMorePositions: false,

      locationsLimit: 4,
      placesLimit: 4,
      positionsLimit: 4,
    }
  },
  computed: {
    limitedLocations() {
      return this.locations.slice(0, this.locationsLimit)
    },
    limitedPlaces() {
      return this.places.slice(0, this.placesLimit)
    },
    limitedPositions() {
      return this.positions.slice(0, this.positionsLimit)
    },
    calcShowMoreLocations() {
      return this.showMoreLocations || !allInList(this.item.locationId, this.limitedLocations)
    },
    calcShowMorePlaces() {
      return this.showMorePlaces || !allInList(this.item.placeId, this.limitedPlaces)
    },
    calcShowMorePositions() {
      return this.showMorePositions || !allInList(this.item.positionIds, this.limitedPositions)
    },
    isNew() {
      // return ['new', 'add'].includes(this.itemId.toString().toLowerCase())
      return isNew(this.itemId)
    },
    url() {  
      return this.$api.resolvePath(['items', this.itemId], { include: 'position' })
    },
    ...mapGetters([
      'positions',
      'timeGroups',
      'locations',
      'places',
      'types',
    ]),
  },
  watch: {
    url: {
      immediate: true,
      handler(newVal) {
        this.fetchData(newVal)
      }
    },
    'item.id'(newItemId, oldItemId) {
      if (newItemId != this.itemId) {
        const routerObj = { name: 'item-edit', params: { itemId: newItemId } }

        if (isNew(oldItemId)) {
          this.$router.replace(routerObj)
        } else {
          this.$router.push(routerObj)
        }
      }
    },
  },
  mounted() {
    this.loadRefData(false)
  },
  methods: {
    findName(val, arr, idFn = z => z.id, nameFn = z => z.name, emptyValue = '', undefinedValue = '?', joinStr = ', ') {
      // val not set
      if (val === undefined || val === null) return emptyValue

      // always make val an array
      val = Array.isArray(val) ? val : [val]

      val = val.map(valItem => {
        const item = arr.find(z => valItem == idFn(z))
        if (!item) return undefinedValue
        return nameFn(item)
      })

      return val.join(joinStr)
    },
    cleanItem(item) {
      let clone = cloneDeep(Object.assign({}, item, { positionIds: item.positions.sort((a, b) => a.itemPosition.order - b.itemPosition.order).map(p => p.id) }))

      const removeFields = ['updatedAt', 'createdAt', 'legacyJson', 'positions']
      removeFields.forEach(field => delete clone[field])

      return clone
    },
    loadRefData(force = false) {
      // load ref data
      const refs = [
        { url: this.$api.resolvePath(`ref/positions`), mutation: 'setPositions', getter: 'positions' },
        { url: this.$api.resolvePath(`ref/locations`), mutation: 'setLocations', getter: 'locations' },
        { url: this.$api.resolvePath(`ref/places`), mutation: 'setPlaces', getter: 'places' },
        { url: this.$api.resolvePath(`ref/timeGroups`), mutation: 'setTimeGroups', getter: 'timeGroups' },
        { url: this.$api.resolvePath(`ref/types`), mutation: 'setTypes', getter: 'types' },
      ]
      
      refs.forEach(({ url, mutation, getter }) => {
        if (force || !this.$store.getters[getter] || this.$store.getters[getter].length < 1) {
          this.$api.get(url)
            .then(res => res.json())
            .then(data => {
              this.$store.commit(mutation, data)
            })
            .catch(err => console.error(err))
        }
      })
    },
    fetchData(url) {
      if (this.isNew) {
        const newItem = cloneDeep(DEFAULT_ITEM)
        this.item = newItem
        return false
      }
      this.isLoading = true
      
      this.$api.get(url)
        .then(res => res.json())
        .then(data => this.cleanItem(data))
        .then(item => this.item = item)
        .then(data => {
          this.item = data
        })
        .catch(err => console.error(err))
        .finally(() => this.isLoading = false)
    },
    submitApi(isNew, payload) {
      if (isNew) {
        return this.$api.post(
          this.$api.resolvePath(`items`),
          JSON.stringify(payload)
        )
      } else {
        return this.$api.put(
          this.$api.resolvePath(['items', payload.id]),
          JSON.stringify(payload)
        )
      }
    },
    submit() {
      this.isSubmitting = true
      let payload = cloneDeep(this.item)
      const isNew = this.isNew

      console.log('submitting', payload)

      this.submitApi(isNew, payload)
      // this.$api.get(
      //   isNew ? this.$api.resolvePath(`items`) : this.$api.resolvePath(['items', payload.id]),
      //   {
      //     method: isNew ? 'POST' : 'PUT',
      //     headers: {
      //       'Content-Type': 'application/json'
      //     },
      //     body: JSON.stringify(payload)
      //   }
      // )
        .then(response => response.json())
        .then(data => this.cleanItem(data))
        .then(item => {
          this.item = item
          return item
        })
        .then(item => this.$store.dispatch('addSnackbar', { 
          type: 'success',
          // timeout: 0,
          message: `${isNew ? 'Created' : 'Updated'} item (${item.id})` 
        }))
        .catch(err => {
          console.error(err)
          this.$store.dispatch('addSnackbar', { 
            type: 'error',
            timeout: 0,
            message: err.message
          })
        })
        .finally(() => this.isSubmitting = false)
    },
  },
}
</script>

<style lang="scss" scoped>
.btn-group-container {
  margin: 16px;

  .btn-group-title {
    font-size: 0.9rem;
    padding: 4px 2px 2px;
  }

  .btn-group-note {
    font-size: 0.7rem;
    padding: 2px 2px 4px;
    min-height: calc(0.7rem * 1.5 + 2px + 4px);

    color: rgba(0, 0, 0, 0.6) !important;
  }

  ::v-deep .j-date-field .v-btn {
    color: inherit;
    border: 1px solid rgba(0, 0, 0, 0.12) !important;
    box-shadow: 0px 0px 0px 0px rgba(0, 0, 0, 0.2), 0px 0px 0px 0px rgba(0, 0, 0, 0.14), 0px 0px 0px 0px rgba(0, 0, 0, 0.12) !important;
  }
}
</style>